import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useOrders } from '../../hooks/useOrders';
import { useCustomerPortal } from '../../hooks/useCustomerPortal';
import { useSubscription } from '../../hooks/useSubscription';
import { SettingsLayout } from './SettingsLayout';
import { Separator } from '../../components/ui/separator';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Check, ExternalLink, FileText } from 'lucide-react';
import { GradientBorder } from '../../components/ui/gradient-border';
import { API_URL } from '../../lib/api';

async function openInvoice(orderId: string) {
  const response = await fetch(
    `${API_URL}/api/v2/cloud/orders/${orderId}/invoice`,
    { credentials: 'include' },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch invoice');
  }

  const data = await response.json();
  if (!data.url) {
    throw new Error('No invoice URL available');
  }
  window.open(data.url, '_blank');
}

export function SettingsBillingPage() {
  const navigate = useNavigate();
  const { plans, planType } = useSubscription();
  const { orders, isLoading: ordersLoading } = useOrders();
  const { openCustomerPortal } = useCustomerPortal();
  const [error, setError] = useState('');

  return (
    <SettingsLayout activeTab="billing">
      <div className="space-y-8">
        <h1 className="text-2xl font-semibold text-white">Billing</h1>

        {error && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Payment Method — only shown for paid plans with a Polar account */}
        {planType !== 'free' && (
          <>
            <section>
              <h2 className="text-lg font-semibold text-white mb-1">
                Payment Method
              </h2>
              <p className="text-sm text-white/50 mb-4">
                Manage your payment method.
              </p>

              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await openCustomerPortal();
                  } catch {
                    setError('Failed to open payment portal. Please try again.');
                  }
                }}
              >
                <ExternalLink className="h-4 w-4" />
                Update payment method
              </Button>
            </section>

            <Separator />
          </>
        )}

        {/* Order History */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-1">
            Order History
          </h2>
          <p className="text-sm text-white/50 mb-4">
            Your past purchases and subscriptions.
          </p>

          {ordersLoading ? (
            <div className="text-center py-8 text-white/50 text-sm">
              Loading orders...
            </div>
          ) : orders.length === 0 ? (
            <GradientBorder innerClassName="w-full py-10 flex flex-col items-center gap-4">
              <p className="text-white/50 text-sm">No orders yet.</p>
              <Button
                variant="outline"
                onClick={() => navigate({ to: '/settings/plans' })}
              >
                Explore Plans
              </Button>
            </GradientBorder>
          ) : (
            <GradientBorder innerClassName="overflow-hidden">
              <Table>
                <TableHeader className="bg-black/30">
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount (inc. tax)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="text-white/60">
                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </TableCell>
                      <TableCell className="text-white">
                        {(() => {
                          const name = order.product?.name ?? 'Purchase';
                          const planNames = plans.map((p) => p.name);
                          if (planNames.includes(name)) {
                            const month = new Date(
                              order.createdAt,
                            ).toLocaleDateString('en-US', {
                              month: 'short',
                              year: 'numeric',
                            });
                            return `${name} \u2014 ${month}`;
                          }
                          return name;
                        })()}
                      </TableCell>
                      <TableCell className="text-white tabular-nums">
                        ${(order.totalAmount / 100).toFixed(2)}{' '}
                        <span className="text-white/50 uppercase text-xs">
                          {order.currency}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-green-500/15 text-green-400 capitalize">
                          <Check className="h-3 w-3" />
                          {order.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await openInvoice(order.id);
                            } catch {
                              setError(
                                'Failed to open invoice. Please try again.',
                              );
                            }
                          }}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GradientBorder>
          )}
        </section>
      </div>
    </SettingsLayout>
  );
}
